import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Loader2, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";
import { Card, Field } from "@/pages/admin/AdminProfile";

const empty = { quote: "", author_name: "", author_role: "", author_company: "", author_avatar_url: "", rating: 5, order: 0 };

export default function AdminTestimonials() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        setItems((await api.get("/testimonials")).data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this testimonial?")) return;
        await api.delete(`/admin/testimonials/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-testimonials-page">
            <Header
                title="Testimonials"
                subtitle="Kutipan dari client, kolega, atau atasan — social proof yang tampil di section 'What clients say'."
                action={
                    <button onClick={() => setEditing({ ...empty })} data-testid="testimonial-new" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand text-white hover:bg-brand-hover">
                        <Plus size={16} /> New testimonial
                    </button>
                }
            />
            {loading ? <div className="mono-label">Loading…</div> : (
                <div className="space-y-3">
                    {items.map((t) => (
                        <div key={t.id} className="p-5 rounded-xl border border-white/5 bg-ink-800/40 flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {t.author_avatar_url ? <img src={t.author_avatar_url} alt="" className="w-full h-full object-contain p-1" /> : <span className="font-display text-lg text-black">{t.author_name[0]}</span>}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-ink-100 text-sm line-clamp-2 italic">&ldquo;{t.quote}&rdquo;</p>
                                <div className="mt-2 flex items-center gap-2 text-xs text-ink-300">
                                    <span className="text-white font-medium">{t.author_name}</span>
                                    {t.author_role && <span>· {t.author_role}</span>}
                                    {t.author_company && <span>· {t.author_company}</span>}
                                    <span className="flex gap-0.5 ml-2">
                                        {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={10} className="text-brand fill-brand" />)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => setEditing({ ...t })} className="p-2 rounded hover:bg-white/10 text-ink-200 hover:text-white"><Pencil size={14} /></button>
                                <button onClick={() => remove(t.id)} className="p-2 rounded hover:bg-red-500/10 text-ink-200 hover:text-red-400"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && <div className="p-12 text-center text-ink-300 border border-dashed border-white/10 rounded-xl">No testimonials yet.</div>}
                </div>
            )}
            {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
        </div>
    );
}

function Editor({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const u = (k) => (e) => setData((s) => ({ ...s, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.put(`/admin/testimonials/${initial.id}`, data);
            else await api.post("/admin/testimonials", data);
            toast.success(isEdit ? "Updated" : "Created");
            onSaved();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70 backdrop-blur" data-testid="testimonial-editor">
            <div className="w-full max-w-xl bg-ink-900 border-l border-white/10 overflow-y-auto">
                <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="mono-label">{isEdit ? "Edit testimonial" : "New testimonial"}</div>
                        <h2 className="font-display text-xl text-white">{data.author_name || "Anonymous"}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} disabled={saving} data-testid="testimonial-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10 text-ink-200"><X size={16} /></button>
                    </div>
                </div>
                <div className="p-6 space-y-5">
                    <Card title="Testimonial">
                        <Field label="Quote"><textarea rows={5} className="ainp" value={data.quote} onChange={u("quote")} /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Author name"><input className="ainp" value={data.author_name} onChange={u("author_name")} /></Field>
                            <Field label="Role"><input className="ainp" value={data.author_role} onChange={u("author_role")} /></Field>
                            <Field label="Company"><input className="ainp" value={data.author_company} onChange={u("author_company")} /></Field>
                            <Field label="Rating (1-5)"><input type="number" min="1" max="5" className="ainp" value={data.rating} onChange={u("rating")} /></Field>
                        </div>
                        <Field label="Avatar URL (tip: pakai logo.clearbit.com/{domain})">
                            <input className="ainp" value={data.author_avatar_url} onChange={u("author_avatar_url")} placeholder="https://logo.clearbit.com/example.com" />
                        </Field>
                        <Field label="Order"><input type="number" className="ainp" value={data.order} onChange={u("order")} /></Field>
                    </Card>
                </div>
            </div>
        </div>
    );
}
